/**
 * THE WRITE CONTRACT — the shapes a caller AUTHORS, and the shapes it reads back.
 *
 * `role-coverage-view.schema.ts` is what a reader of a percentage gets;
 * `role-coverage-authoring-view.schema.ts` is what a repairer of a broken model
 * gets. Both are READS. This file is the third side and until it existed the
 * whole money model was unauthorable: three tables shipped with a migration, a
 * read path and no way for the product to put a single number in any of them.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * A WRITE BOUNDARY VALIDATES. IT DOES NOT DEGRADE.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Every read in this module is built to survive a stored model that does not
 * parse — it contributes zero, names itself, and the percentage still renders
 * with a warning attached. That is correct for a read, and it is exactly wrong
 * here. `CoverageFormulaSchema` is embedded directly in every model field
 * below, so a formula naming a function outside `COVERAGE_FUNCTIONS`, or an
 * input key nothing declares, is refused with a 400 and never reaches a column.
 *
 * The refusal is the point. A silently accepted model becomes a
 * `WORKLOAD_MODEL_INVALID` warning on the next read — minutes later, on a
 * different screen, to a different person, with the author long gone. On a live
 * demo it becomes a percentage that will not appear and nobody can explain.
 * `CoverageFormulaSchema.superRefine` already names the offending path
 * (`steps.2.args.0: reference to undeclared input "runsPerYer"`), and
 * `formatZodError` carries that straight to the client, so the UI can highlight
 * one field rather than reject a form.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * EVERY OPTIONAL MODEL IS REQUIRED-AND-NULLABLE. NOTHING HERE IS `.optional()`.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * The routes carrying these bodies are `PUT`s over whole rows, so an absent key
 * has to mean something, and both available meanings are wrong to guess at:
 * "leave it as it was" and "clear it" are the two states an author is choosing
 * between, and a schema that lets the key be omitted answers for them.
 *
 * That is not a hypothetical in this backend. `mergeModelConfig` strips and
 * re-applies a HAND-ENUMERATED set of provider fields, so an omitted key in the
 * list CLEARS the stored value while an omitted key outside it FREEZES the
 * stored value — one function, two opposite behaviours, neither visible at a
 * call site. Making the field required and nullable removes the question: a
 * caller clearing its cost model sends `"costFormula": null` and means it.
 *
 * The cost is that a client must send every field. It has every field: the
 * authoring read serves all of them, and a form that holds a row holds the
 * whole row.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THIS BOUNDARY CHECKS STRUCTURE. IT DOES NOT — AND CANNOT — CHECK VALUE.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * A model that evaluates to a negative annual cost is refused, by name, at
 * READ time. Nothing here refuses it, and that is a limit rather than a gap:
 * an authored model is an expression tree whose result is not known until it
 * is evaluated, and evaluation needs the organisation's working-time model —
 * which a Role may legitimately be authored BEFORE its organisation has
 * stated. Refusing here would mean an author cannot save a workload until an
 * admin has configured the org, and would still be wrong later, because a
 * measured input can replace an authored magnitude and move the result after
 * the write.
 *
 * So the two boundaries answer different questions and neither can be folded
 * into the other. A structurally valid model that produces an impossible
 * figure reaches the column, and the read reports the named reason rather
 * than a number — which is the behaviour the coverage engine is built around
 * everywhere else. **Do not "fix" this by evaluating on write.**
 *
 * ⚠️ **A PERIOD BASIS IS STRUCTURE, NOT VALUE, SO THE SENTENCE ABOVE DOES NOT
 * REACH IT.** The two money models below are refused when they count over
 * different windows, and that check needs no working-time model, no currency
 * and no magnitude: a basis is read off `perPeriod` and the shape of the tree,
 * which is the same class of fact as "does this reference resolve". It answers
 * before the organisation has stated a working day, it answers the same thing
 * forever, and no measured input can move it. The argument is written out in
 * `coverage-formula-period-basis.ts`; read it before folding anything else in
 * here, because almost nothing else about a model has that property.
 */
import { z } from "zod";

import { CoverageFormulaSchema } from "./coverage-formula.schema";
import { refuseDisagreeingMoneyPeriodBasis } from "./coverage-money-basis-agreement";
import { CoverageWorkingTimeSettingsSchema } from "./coverage-time-unit.types";

/**
 * ISO 4217 ALPHABETIC, upper case — `EUR`, never `€` and never `eur`.
 *
 * The same predicate the database CHECK applies (`currency IS NULL OR currency
 * ~ '^[A-Z]{3}$'`), restated here rather than left to Postgres for one reason:
 * a constraint violation arrives as a `P2010` and renders as a 500, which tells
 * the caller the server broke when their input was invalid. A 400 naming the
 * field is both correct and cheaper.
 *
 * Deliberately NOT a closed enum of today's codes. ISO 4217 gains and withdraws
 * codes, and an enum would refuse a legitimate currency until somebody shipped
 * a deploy — the same argument `schema.prisma` makes for not using a database
 * enum here. A well-formed code that is not a real currency costs a wrong
 * label; the engine never converts between currencies, so it costs nothing else.
 *
 * `null` is a legal value and NOT a missing one: it is the organisation
 * withdrawing its stated currency, after which every money figure it has
 * reports `NO_CURRENCY` rather than a number in an unknown unit.
 */
export const CoverageCurrencySchema = z.string().regex(/^[A-Z]{3}$/, {
  message: "currency must be an ISO 4217 alphabetic code in upper case, for example EUR"
});

/**
 * The organisation's working-time model and currency, as a caller states them.
 *
 * The three working-time fields come from `CoverageWorkingTimeSettingsSchema`
 * UNCHANGED, extended rather than restated. That schema is what
 * `coverageContextForRole` already refuses an impossible working day with, so
 * restating the bounds here would be a second opinion about what a legal
 * working day is — and the two would agree on the day they were written. A
 * write that this schema accepts is a write the read path can always evaluate.
 */
export const OrganizationAutomationSettingsInputSchema = CoverageWorkingTimeSettingsSchema.extend({
  currency: CoverageCurrencySchema.nullable()
});

/** The organisation's stated model, as the API returns it. */
export const OrganizationAutomationSettingsViewSchema = z.strictObject({
  organizationId: z.string(),
  hoursPerDay: z.number(),
  daysPerWeek: z.number(),
  workingWeeksPerYear: z.number(),
  currency: CoverageCurrencySchema.nullable()
});

/**
 * One overridable working-time field: a positive finite number, or `null` for
 * "this Role does not override it".
 *
 * `null` is the value the resolver already treats as "no override", and it is
 * spelled out rather than left to absence for the reason in
 * `coverage-working-time.resolver.ts`: the override arrives from three NULLABLE
 * columns, so a guard testing only `!== undefined` accepts `null` as a real
 * override and feeds a zero-length working day into a division.
 */
const WorkingTimeOverrideFieldSchema = z
  .number()
  .refine((n) => Number.isFinite(n) && n > 0, {
    message: "a working-time override must be finite and > 0"
  })
  .nullable();

/**
 * A Role's per-field override of the organisation's assumptions.
 *
 * Every field required, every field nullable — see the file header. A Role that
 * works a four-day week on the organisation's standard year sends
 * `{ hoursPerDay: null, daysPerWeek: 4, workingWeeksPerYear: null }`.
 */
export const RoleWorkingTimeOverrideInputSchema = z.strictObject({
  hoursPerDay: WorkingTimeOverrideFieldSchema,
  daysPerWeek: WorkingTimeOverrideFieldSchema,
  workingWeeksPerYear: WorkingTimeOverrideFieldSchema
});

/**
 * One Role's workload model, as a caller authors it.
 *
 * `formula` is NOT nullable and the column is NOT NULL: a workload row that
 * carries no denominator has no reason to exist, and the read path already
 * distinguishes "no row" (`NO_WORKLOAD_MODEL`) from "a row that will not parse"
 * (`WORKLOAD_MODEL_INVALID`). A third state — a row with a null formula — would
 * be a fourth answer nothing downstream names. To remove a workload entirely,
 * the row is deleted, not blanked.
 *
 * The two money models ARE nullable: a Role may state its hours and never its
 * cost, which is a normal state and not a warning.
 */
export const RoleWorkloadInputSchema = z
  .strictObject({
    /** The denominator: person-hours this Role works in a year. */
    formula: CoverageFormulaSchema,
    /** What this Role costs per year — salaries, seats. `null` when uncosted. */
    costFormula: CoverageFormulaSchema.nullable(),
    /** Revenue this Role holds that no system produces. `null` when unmodelled. */
    revenueFormula: CoverageFormulaSchema.nullable(),
    workingTimeOverride: RoleWorkingTimeOverrideInputSchema
  })
  .superRefine(refuseDisagreeingMoneyPeriodBasis);

/**
 * A Role's stored workload, as the API returns it after a write.
 *
 * The three model fields are the VALIDATED formulas, not a re-read of the
 * column. They are the same JSON: the write persisted exactly what the schema
 * above accepted, so parsing the row back would be asserting that the database
 * round-tripped its own input, and the failure branch of that parse would be
 * unreachable and untestable. The row supplies its identity and its timestamp;
 * the caller's own validated models supply the rest.
 */
export const RoleWorkloadViewSchema = z.strictObject({
  id: z.string(),
  roleId: z.string(),
  formula: CoverageFormulaSchema,
  costFormula: CoverageFormulaSchema.nullable(),
  revenueFormula: CoverageFormulaSchema.nullable(),
  workingTimeOverride: RoleWorkingTimeOverrideInputSchema,
  updatedAt: z.string()
});

/**
 * One system's impact model, as a caller authors it.
 *
 * Same required/nullable split as the workload, for the same reasons, and the
 * same NOT NULL on `formula` — `RoleSystemImpact.formula` is the term this row
 * exists to carry.
 *
 * TWO money columns rather than one signed net, and nothing here may collapse
 * them. A net of zero built from `€0`/`€0` and one built from `€400`/`€400` are
 * different facts, and "declares no revenue model" has to stay expressible.
 */
export const RoleSystemImpactInputSchema = z
  .strictObject({
    /** Person-hours per year this system removes from the Role's workload. */
    formula: CoverageFormulaSchema,
    /** Money this system generates per year. `null` when unmodelled. */
    revenueFormula: CoverageFormulaSchema.nullable(),
    /** Money this system costs per year. `null` when unmodelled. */
    costFormula: CoverageFormulaSchema.nullable()
  })
  .superRefine(refuseDisagreeingMoneyPeriodBasis);

/** One system's stored impact model, as the API returns it after a write. */
export const RoleSystemImpactViewSchema = z.strictObject({
  id: z.string(),
  /** The `RoleResource` row — the same id every coverage warning names. */
  roleResourceId: z.string(),
  /** The polymorphic pair naming the system itself. */
  resourceType: z.string(),
  resourceId: z.string(),
  formula: CoverageFormulaSchema,
  revenueFormula: CoverageFormulaSchema.nullable(),
  costFormula: CoverageFormulaSchema.nullable(),
  updatedAt: z.string()
});

/** The organisation's working-time model and currency, on the way in. */
export type OrganizationAutomationSettingsInput = z.infer<
  typeof OrganizationAutomationSettingsInputSchema
>;

/** The organisation's stated model, as the API returns it. */
export type OrganizationAutomationSettingsView = z.infer<
  typeof OrganizationAutomationSettingsViewSchema
>;

/** A Role's per-field override of the organisation's assumptions, on the way in. */
export type RoleWorkingTimeOverrideInput = z.infer<typeof RoleWorkingTimeOverrideInputSchema>;

/** One Role's workload model, on the way in. */
export type RoleWorkloadInput = z.infer<typeof RoleWorkloadInputSchema>;

/** One Role's stored workload, as the API returns it. */
export type RoleWorkloadView = z.infer<typeof RoleWorkloadViewSchema>;

/** One system's impact model, on the way in. */
export type RoleSystemImpactInput = z.infer<typeof RoleSystemImpactInputSchema>;

/** One system's stored impact model, as the API returns it. */
export type RoleSystemImpactView = z.infer<typeof RoleSystemImpactViewSchema>;
